import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Loader2, AlertCircle, Copy, Save, Check, CheckCircle, ListPlus, Printer
} from 'lucide-react';
import axios from 'axios';
import QRCode from 'qrcode';
import { BarcodeStartPositionModal } from './BarcodeStartPositionModal';
import { stripItemIdPrefix } from '../../lib/itemId';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', {
  maximumFractionDigits: 2,
})}`;

const buildItemEdits = (items = []) => Object.fromEntries(items.map(item => [
  item.id,
  {
    sale_rate: item.sale_rate ?? '',
    display_rate: item.display_rate ?? '',
    sub_rack_id: item.sub_rack_id ?? '',
    slot_number: item.slot_number ?? '',
    asset_id: item.asset_id ?? '',
    mrp: item.mrp ?? '',
  },
]));

const comparableEdits = edits => JSON.stringify(Object.keys(edits).sort().map(itemId => {
  const item = edits[itemId] || {};
  const numberValue = value => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : String(value);
  };
  return [
    itemId,
    numberValue(item.sale_rate),
    numberValue(item.display_rate),
    item.sub_rack_id || '',
    numberValue(item.slot_number),
    String(item.asset_id || '').trim(),
    numberValue(item.mrp),
  ];
}));

export const InwardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [subRacks, setSubRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pendingBarcodePrint, setPendingBarcodePrint] = useState(null);
  const [queuedItemIds, setQueuedItemIds] = useState(new Set());
  const [queueSavingId, setQueueSavingId] = useState('');

  // Editable per-item data: { [item.id]: { sale_rate, display_rate, sub_rack_id } }
  const [itemEdits, setItemEdits] = useState({});
  const [savedItemEdits, setSavedItemEdits] = useState({});
  const [itemSortOrder, setItemSortOrder] = useState('oldest');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [detailRes, racksRes, queueRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/inward/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/racks/sub-racks/all`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/inward/printing-queue`, getAuthHeaders()).catch(() => ({ data: [] })),
        ]);
        const data = detailRes.data;
        setEntry(data);
        setSubRacks(racksRes.data);
        setQueuedItemIds(new Set((queueRes.data || []).map(item => item.item_db_id)));
        // Initialize edits from existing item data
        const edits = buildItemEdits(data.items);
        setItemEdits(edits);
        setSavedItemEdits(edits);
      } catch (err) {
        setError('Failed to fetch inward details');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const statusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-700';
      case 'sold': return 'bg-blue-100 text-blue-700';
      case 'rented': return 'bg-purple-100 text-purple-700';
      case 'servicing': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const updateItemField = (itemId, field, value) => {
    setItemEdits(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const copyToAll = (sourceItemId) => {
    const source = itemEdits[sourceItemId];
    if (!source) return;
    setItemEdits(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        // Asset ID is per-item-unique; preserve each item's own value
        next[key] = {
          ...source,
          asset_id: next[key]?.asset_id ?? '',
        };
      });
      return next;
    });
  };

  const handleSaveBatch = async () => {
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      const items = Object.entries(itemEdits).map(([itemId, data]) => ({
        item_db_id: itemId,
        sale_rate: data.sale_rate !== '' ? parseFloat(data.sale_rate) : null,
        display_rate: data.display_rate !== '' ? parseFloat(data.display_rate) : null,
        sub_rack_id: data.sub_rack_id || null,
        slot_number: data.slot_number !== '' ? Number(data.slot_number) : null,
        asset_id: (data.asset_id || '').trim() || null,
        mrp: data.mrp !== '' ? parseFloat(data.mrp) : null,
      }));
      const res = await axios.put(
        `${BACKEND_URL}/api/inward/${id}/save-batch`,
        { items },
        getAuthHeaders()
      );
      setEntry(res.data);
      const savedEdits = buildItemEdits(res.data.items);
      setItemEdits(savedEdits);
      setSavedItemEdits(savedEdits);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const isComplete = entry?.batch_status === 'Complete';
  const hasChanges = comparableEdits(itemEdits) !== comparableEdits(savedItemEdits);

  const printItemBarcode = useCallback(async (items, title, startPosition = 1) => {
    if (!items.length) { alert('No items to print'); return; }
    const QR_CSS = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { margin: 0; }
      body { width: 83mm; font-family: Arial, sans-serif; background: #fff; }
      .grid {
        width: 83mm;
        margin-left: 11mm;
        margin-top: 0;
        display: grid;
        grid-template-columns: repeat(5, 16mm);
        column-gap: 0;
        row-gap: 5mm;
        padding: 0 1.5mm;
      }
      .qr-card {
        width: 16mm; height: 16mm;
        display: flex; align-items: center; justify-content: center;
        page-break-inside: avoid;
      }
      .qr-content {
        width: 16mm; height: 13.2mm;
        display: inline-flex; flex-direction: column;
        align-items: center; justify-content: center;
      }
      .qr-img { width: 10mm; height: 10mm; display: block; flex: none; }
      .code-text {
        width: 12mm; height: 2.2mm; line-height: 2.2mm;
        font-size: 6pt; font-family: monospace; margin-top: 1mm;
        text-align: center; white-space: nowrap; overflow: hidden;
      }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `;
    const printWin = window.open('', '_blank', 'width=600,height=400');
    printWin.document.write(`<!DOCTYPE html><html><head><title>QR Codes - ${title}</title><style>${QR_CSS}</style></head><body><div class="grid">`);

    for (let slot = 1; slot < startPosition; slot += 1) {
      const emptyCard = printWin.document.createElement('div');
      emptyCard.className = 'qr-card';
      printWin.document.querySelector('.grid').appendChild(emptyCard);
    }

    for (const item of items) {
      try {
        const barcodeValue = stripItemIdPrefix(item.item_id);
        if (!barcodeValue) continue;
        const dataUrl = await QRCode.toDataURL(barcodeValue, { width: 120, margin: 1, errorCorrectionLevel: 'M' });
        const card = printWin.document.createElement('div');
        card.className = 'qr-card';
        card.innerHTML = `<div class="qr-content"><img class="qr-img" src="${dataUrl}" alt="QR"/><div class="code-text">${barcodeValue}</div></div>`;
        printWin.document.querySelector('.grid').appendChild(card);
      } catch { /* skip */ }
    }

    printWin.document.write('</div></body></html>');
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 400);
  }, []);

  const printAllBarcodes = useCallback(() => {
    if (!entry?.items?.length) { alert('No items to print'); return; }
    setPendingBarcodePrint({ items: entry.items, title: entry.product_name || entry.batch_number });
  }, [entry]);

  const printSingleBarcode = useCallback((item) => {
    setPendingBarcodePrint({ items: [item], title: item.item_id });
  }, []);

  const addItemToPrintingQueue = async (item) => {
    if (queuedItemIds.has(item.id)) return;
    setQueueSavingId(item.id);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/inward/printing-queue/items/${item.id}`, {}, getAuthHeaders());
      setQueuedItemIds(current => new Set([...current, item.id]));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add item to the printing queue');
    } finally {
      setQueueSavingId('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error && !entry) {
    return (
      <div data-testid="inward-detail-error">
        <button onClick={() => navigate('/admin/inward')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Inward
        </button>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
          <AlertCircle className="h-5 w-5" />
          <span>{error || 'Entry not found'}</span>
        </div>
      </div>
    );
  }

  if (!entry) return null;

  const hasPurchaseRate = entry.purchase_rate !== null
    && entry.purchase_rate !== undefined
    && entry.purchase_rate !== '';
  const purchaseRate = hasPurchaseRate ? Number(entry.purchase_rate) : 0;
  const taxPercentage = Number(entry.tax_percentage || 0);
  const quantity = Number(entry.quantity || 0);
  const singlePieceGst = purchaseRate * taxPercentage / 100;
  const singlePieceTotal = purchaseRate + singlePieceGst;
  const allPiecesAmount = purchaseRate * quantity;
  const allPiecesGst = singlePieceGst * quantity;
  const allPiecesTotal = allPiecesAmount + allPiecesGst;
  const quantityLabel = `${quantity.toLocaleString('en-IN')} ${quantity === 1 ? 'piece' : 'pieces'}`;
  const displayPurchaseAmount = (value) => hasPurchaseRate ? formatCurrency(value) : '-';

  return (
    <div data-testid="inward-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/inward')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Inward
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inward Details</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider ${
              isComplete ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-700 border border-amber-200'
            }`} data-testid="batch-status-badge">
              {isComplete && <CheckCircle className="h-3.5 w-3.5" />}
              {isComplete ? 'Complete' : 'Pending'}
            </span>
          </div>
          <p className="text-slate-600 mt-1 font-mono text-lg">{entry.batch_number}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-500">
            Created: {formatDateTime(entry.created_at)}
          </div>
          {entry.items?.length > 0 && (
            <button onClick={printAllBarcodes} data-testid="print-all-barcodes-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors">
              <Printer className="h-3.5 w-3.5" /> Print Barcodes
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Success */}
      {saveSuccess && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-6" data-testid="save-success-msg">
          <CheckCircle className="h-5 w-5" />
          <span>{isComplete
            ? 'Batch saved successfully. Inventory updated and batch marked as Complete.'
            : 'Changes saved. The batch remains Pending until Sale Rate and Display Rate are entered for every item.'}</span>
        </div>
      )}

      {/* Batch Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <InfoCard label="Product" value={entry.product_name} sub={entry.product_code} />
        <InfoCard label="Batch Number" value={entry.batch_number} mono />
        <InfoCard label="Quantity" value={entry.quantity} />
        <InfoCard label="Vendor" value={entry.vendor_name || '-'} />
        <InfoCard label="Inward Firm" value={entry.firm_name || '-'} />
        <InfoCard label="Tax" value={entry.tax_name ? `${entry.tax_name} (${entry.tax_percentage}%)` : '-'} />
        {/* Per-piece and complete-batch purchase pricing */}
        <div className="bg-white rounded-sm border border-slate-200 col-span-2 md:col-span-3 lg:col-span-4" data-testid="purchase-pricing-section">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Purchase Pricing</div>
          </div>

          <div className="p-4" data-testid="single-piece-pricing">
            <div className="text-sm font-semibold text-slate-900 mb-3">Single Piece</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <PricingValue label="Without GST" value={displayPurchaseAmount(purchaseRate)} />
              <PricingValue label="GST Percentage" value={`${taxPercentage}%`} />
              <PricingValue label="GST Amount" value={displayPurchaseAmount(singlePieceGst)} />
              <PricingValue label="Total" value={displayPurchaseAmount(singlePieceTotal)} highlight />
            </div>
          </div>

          <div className="p-4 border-t border-slate-200 bg-slate-50/50" data-testid="all-pieces-pricing">
            <div className="text-sm font-semibold text-slate-900 mb-3">All Pieces ({quantityLabel})</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <PricingValue label="Without GST" value={displayPurchaseAmount(allPiecesAmount)} />
              <PricingValue label="GST Percentage" value={`${taxPercentage}%`} />
              <PricingValue label="GST Amount" value={displayPurchaseAmount(allPiecesGst)} />
              <PricingValue label="Total" value={displayPurchaseAmount(allPiecesTotal)} highlight />
            </div>
          </div>
        </div>
        <InfoCard label="Invoice Date" value={formatDate(entry.invoice_date)} />
        <InfoCard label="Invoice Number" value={entry.invoice_number || '-'} />
        <InfoCard label="Payment Terms" value={entry.payment_term_name || '-'} />
        <InfoCard label="Sale Rate" value={entry.sale_rate ? `₹${entry.sale_rate.toLocaleString()}` : '-'} />
        <InfoCard label="Display Rate" value={entry.display_rate ? `₹${entry.display_rate.toLocaleString()}` : '-'} highlight />
      </div>

      {/* Items Section */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
            Individual Items
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              {entry.items?.length || 0} item{(entry.items?.length || 0) !== 1 ? 's' : ''}
            </span>
            <div className="flex rounded-sm border border-slate-200 overflow-hidden text-xs font-semibold">
              <button onClick={() => setItemSortOrder('oldest')} data-testid="sort-oldest"
                className={`px-3 py-1.5 transition-colors ${itemSortOrder === 'oldest' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                Oldest First
              </button>
              <button onClick={() => setItemSortOrder('newest')} data-testid="sort-newest"
                className={`px-3 py-1.5 transition-colors border-l border-slate-200 ${itemSortOrder === 'newest' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                Newest First
              </button>
            </div>
          </div>
        </div>

        {!entry.items || entry.items.length === 0 ? (
          <div className="text-center py-12" data-testid="no-items">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No individual items generated for this batch</p>
            <p className="text-slate-400 text-sm mt-1">Item generation was turned off during inward</p>
          </div>
        ) : (
          <>
            {/* Items Table Header */}
            <div className="hidden md:grid gap-3 px-6 py-3 border-b border-slate-200 bg-slate-50 items-center md:grid-cols-[40px_minmax(0,1.3fr)_minmax(0,1.3fr)_90px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)_80px_96px]">
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">#</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Item ID</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Asset ID <span className="text-slate-400 normal-case font-normal">(opt.)</span></div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">MRP (₹) <span className="text-slate-400 normal-case font-normal">(opt.)</span></div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Sale Rate (₹)</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Display Rate (₹)</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Rack / Sub-Rack</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Slot</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Actions</div>
            </div>

            {/* Items Rows */}
            {[...entry.items].sort((a, b) => {
              const cmp = (a.item_id || '').localeCompare(b.item_id || '');
              return itemSortOrder === 'newest' ? -cmp : cmp;
            }).map((item, idx) => {
              const edit = itemEdits[item.id] || { sale_rate: '', display_rate: '', sub_rack_id: '', slot_number: '', asset_id: '', mrp: '' };
              const selectedSubRack = subRacks.find(sr => sr.id === edit.sub_rack_id);
              const slotCount = Number(selectedSubRack?.slot_count) || 0;
              return (
                <div
                  key={item.id}
                  data-testid={`item-row-${item.item_id}`}
                  className="grid grid-cols-1 md:gap-3 gap-2 px-6 py-3 border-b border-slate-100 hover:bg-slate-50 items-center md:grid-cols-[40px_minmax(0,1.3fr)_minmax(0,1.3fr)_90px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)_80px_96px]"
                >
                  <div className="text-slate-400 text-sm">{idx + 1}</div>
                  <div>
                    <span className="font-mono font-medium text-slate-900 text-sm">{item.item_id}</span>
                    {item.created_at && (
                      <div className="text-xs text-slate-400 mt-0.5">{formatDate(item.created_at)}</div>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={edit.asset_id}
                      onChange={e => updateItemField(item.id, 'asset_id', e.target.value)}
                      placeholder="Asset ID"
                      data-testid={`item-asset-id-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none font-mono"
                    />
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor(item.status)}`}>
                      {item.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <input
                      type="number"
                      value={edit.mrp}
                      onChange={e => updateItemField(item.id, 'mrp', e.target.value)}
                      placeholder="MRP"
                      step="0.01"
                      min="0"
                      data-testid={`item-mrp-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      value={edit.sale_rate}
                      onChange={e => updateItemField(item.id, 'sale_rate', e.target.value)}
                      placeholder="Sale Rate"
                      step="0.01"
                      min="0"
                      data-testid={`item-sale-rate-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      value={edit.display_rate}
                      onChange={e => updateItemField(item.id, 'display_rate', e.target.value)}
                      placeholder="Display Rate"
                      step="0.01"
                      min="0"
                      data-testid={`item-display-rate-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <select
                      value={edit.sub_rack_id}
                      onChange={e => setItemEdits(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          sub_rack_id: e.target.value,
                          slot_number: '',
                        },
                      }))}
                      data-testid={`item-sub-rack-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none"
                    >
                      <option value="">-- Select Sub-Rack --</option>
                      {subRacks.map(sr => (
                        <option key={sr.id} value={sr.id}>{sr.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={edit.slot_number}
                      onChange={e => updateItemField(item.id, 'slot_number', e.target.value)}
                      disabled={!edit.sub_rack_id || slotCount === 0}
                      data-testid={`item-slot-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <option value="">{!edit.sub_rack_id ? '--' : slotCount ? 'Slot' : 'No slots'}</option>
                      {Array.from({ length: slotCount }, (_, slotIndex) => slotIndex + 1).map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => addItemToPrintingQueue(item)}
                      disabled={queuedItemIds.has(item.id) || queueSavingId === item.id}
                      data-testid={`add-printing-queue-${idx}`}
                      title={queuedItemIds.has(item.id) ? 'Already in Printing Queue' : 'Add to Printing Queue'}
                      className={`inline-flex items-center border p-1.5 rounded-sm transition-colors disabled:cursor-default ${queuedItemIds.has(item.id) ? 'border-green-200 bg-green-50 text-green-600' : 'border-slate-200 text-slate-500 hover:text-orange-600 hover:bg-orange-50'}`}
                    >
                      {queueSavingId === item.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : queuedItemIds.has(item.id)
                        ? <Check className="h-3.5 w-3.5" />
                        : <ListPlus className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToAll(item.id)}
                      data-testid={`copy-to-all-${idx}`}
                      title="Copy rates &amp; rack to all items"
                      className="inline-flex items-center text-slate-500 hover:text-orange-600 border border-slate-200 p-1.5 rounded-sm hover:bg-orange-50 transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => printSingleBarcode(item)}
                      data-testid={`print-barcode-${idx}`}
                      title="Print Barcode"
                      className="inline-flex items-center text-slate-500 hover:text-orange-600 border border-slate-200 p-1.5 rounded-sm hover:bg-orange-50 transition-colors"
                    >
                      <Printer className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Save Batch Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {!hasChanges
                  ? 'Make a change to enable Save Batch.'
                  : isComplete
                  ? 'This batch has been saved. You can still update and re-save.'
                  : 'Sale Rate and Display Rate are required for every item before the batch is marked Complete.'}
              </p>
              <button
                onClick={handleSaveBatch}
                disabled={saving || !hasChanges}
                data-testid="save-batch-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-6 py-2.5 rounded-none hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Batch
              </button>
            </div>
          </>
        )}
      </div>

      <BarcodeStartPositionModal
        open={Boolean(pendingBarcodePrint)}
        onClose={() => setPendingBarcodePrint(null)}
        onConfirm={(position) => {
          const printRequest = pendingBarcodePrint;
          setPendingBarcodePrint(null);
          if (printRequest) printItemBarcode(printRequest.items, printRequest.title, position);
        }}
      />
    </div>
  );
};

const InfoCard = ({ label, value, sub, mono, highlight }) => (
  <div className="bg-white rounded-sm border border-slate-200 p-4">
    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-base font-medium ${highlight ? 'text-orange-600' : 'text-slate-900'} ${mono ? 'font-mono' : ''}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-slate-400 font-mono mt-0.5">{sub}</div>}
  </div>
);

const PricingValue = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-base font-medium ${highlight ? 'text-orange-600' : 'text-slate-900'}`}>
      {value}
    </div>
  </div>
);
